// filepath: apps/web/src/features/lists/kanban/KanbanBoard.tsx
//
// FR-UI-VIEW-1 AC-1..AC-6 — top-level Kanban renderer.
//
// Owns the 6-state envelope for the Kanban view (FR-UI-CORE-10 +
// FR-UI-VIEW-1 AC-4):
//
//   - loading               → SkeletonLayout
//   - fetch-failure         → FetchError
//   - permission-denied     → PermissionDenied
//   - stale-version         → StaleVersionError
//   - validation-error      → ValidationError (per AC-4)
//   - rate-limit            → RateLimitBackpressure
//   - ready                 → N columns × KanbanCard(s)
//
// Branches from EntityListPage when the resolved
// `entity_definition.definition_json.view_config.default_view_type` ==
// "kanban". For views other than kanban (the table / tree / gantt
// surfaces, owned by FR-UI-VIEW-2/3), the EntityListPage continues
// to render its existing chrome.
//
// SECURITY / INVARIANT REMINDERS:
//   - INV-1 (tenantSlug cache keys): useKanbanRecords handles this.
//   - INV-2 (server-stripped rendering): KanbanCard iterates
//     Object.keys(record.fields); the board passes the records
//     through as-is.
//   - INV-7 (row_version OCC): useKanbanMove carries the row_version
//     in the PUT body per internal/entity/record_handlers.go
//     UpdateRecordRequest. The board threads the column reassign
//     through `useKanbanMove`.
//   - INV-9 (text-only): no dangerouslySetInnerHTML anywhere.
//   - The board does NOT mutate the cache optimistically. The card
//     moves stay in their original column until the server returns
//     200; on 4xx/5xx the parent calls `onError` and rolls back.
//     This keeps the optimistic-state surface visible at the call
//     site (see AC-6 — no silent column jumps).
//
// STATUS ROUTING:
//   The Kanban-receive errors are NOT the same shape as the
//   entity-definition-fetch errors. The records-fetch errors route
//   through `classifyListError` (mirror of EntityListPage); the
//   validation errors route through the ValidationError primitive
//   when a 422 surfaces (e.g. invalid_kanban_group_by during the
//   PUT round-trip — see useKanbanMove's 422 mapping).

import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import {
  ApiError,
  HttpConflictError,
  HttpValidationError,
} from "@/definitions/types";
import { SkeletonLayout } from "@/design-system/error-states/SkeletonLayout";
import { FetchError } from "@/design-system/error-states/FetchError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { RateLimitBackpressure } from "@/design-system/error-states/RateLimitBackpressure";
import { ValidationError } from "@/design-system/error-states/ValidationError";
import { KanbanColumn, type KanbanRecord } from "./KanbanColumn";
import { KanbanCountBanner } from "./KanbanCountBanner";
import { useKanbanRecords, KANBAN_RECORDS_CAP } from "./hooks/useKanbanRecords";
import {
  useKanbanDragDrop,
  type KanbanMoveInput,
} from "./hooks/useKanbanDragDrop";
import type {
  EntityDefinitionWire,
  EntityListStatus,
  WireFieldDefinition,
} from "../types";
import type { EntityViewConfigWire, EntityKanbanConfigWire } from "./types";

export interface KanbanBoardProps {
  /** Target entity type id. */
  entityType: string;
  /**
   * Resolved entity definition. Carries the canonical
   * `definition_json.fields` (sort order source) AND the
   * `definition_json.view_config` block (defaults + kanban
   * parameters). Optional — when omitted, the board fetches via
   * the route shell, mirroring EntityListPage's behaviour.
   */
  definition?: EntityDefinitionWire | null;
  /**
   * Optional conflict-resolution opener. When supplied, the board
   * surfaces 409 errors through this callback (FR-UI-CORE-6). The
   * parent (typically App.tsx) supplies the
   * `useConflictResolver.show()` callback so the modal is mounted
   * exactly once across the SPA.
   */
  onConflict?: (error: HttpConflictError, input: KanbanMoveInput) => void;
}

/**
 * Classify a TanStack Query result into one of the 6 mandatory
 * renderer states (FR-UI-CORE-10 + FR-UI-VIEW-1 AC-4). Mirrors
 * EntityListPage's `classifyListError` so the visual surface
 * transitions are identical between table and kanban.
 *
 * - 401 / 403 → permission-denied
 * - 404 + 'active' → permission-denied
 * - 410 → stale-version
 * - 429 → rate-limit
 * - 4xx other / 5xx / network → fetch-failure
 * - 200 → success
 */
function classifyKanbanError(err: ApiError): EntityListStatus {
  if (err.status === 401 || err.status === 403) return "permission-denied";
  if (err.status === 429) return "rate-limit";
  if (err.status === 410) return "stale-version";
  if (err.status === 404) return "permission-denied";
  return "fetch-failure";
}

/**
 * Resolve the columns to render. Walks the enum field's `values`
 * to guarantee every column exists in the canvas (even when the
 * current record set is empty for that value). Falls back to a
 * single "default" column if the values cannot be resolved —
 * conservative; preserves the user's ability to see anything at
 * all when the definition is misconfigured.
 */
function resolveColumns(
  fields: readonly WireFieldDefinition[] | undefined,
  groupByField: string | undefined,
): { value: string; label: string; accent: string }[] {
  if (!groupByField) return [];
  const field = fields?.find((f) => f.name === groupByField);
  const values = field?.values ?? [];
  // Palette — token-derived CSS variables (no raw hex per FR-UI-OPS-3).
  // Visually distinct but still design-system-consistent. The
  // palette is index-based; a record's column index may shift when
  // the enum is reordered, but the visible identity (color) tracks
  // the value (not the index) so the visual stays stable per-column
  // across re-renders.
  const palette = [
    "var(--color-primary)",
    "var(--color-info, var(--blue-500))",
    "var(--color-warning)",
    "var(--color-success, var(--green-500))",
    "var(--color-danger-subtle-text, var(--red-500))",
    "var(--text-tertiary)",
  ];
  return values.map((v, idx) => ({
    value: v,
    label: v,
    accent: palette[idx % palette.length] ?? "var(--text-tertiary)",
  }));
}

/**
 * Resolve the view_config block from the wire shape. Defensive — the
 * field is optional on the wire (older FR-ENT-10 deployments may
 * omit it). When absent, the board renders the empty-state chrome
 * and surfaces "no view config" as a validation-error so the user
 * has a clear signal.
 *
 * Wire path: `definition.definition_json.view_config` —
 * NOT `definition.definition_json` directly. The parent shape carries
 * `view_config` as one of its keys (alongside `entity_type`, `fields`,
 * etc.). SR-O-1 (tracked pre-existing cast on EntityListPage) flagged
 * the earlier cast-misuse pattern; the helper here is rewritten to
 * read `definition.definition_json.view_config` so the data path
 * matches the wire exactly and CAN be removed once FR-ENT-10
 * centralises the wire narrowing.
 */
function resolveViewConfig(
  definition: EntityDefinitionWire | null | undefined,
): EntityViewConfigWire | null {
  if (!definition) return null;
  const defJson = definition.definition_json;
  if (!defJson) return null;
  // Wire path: definition_json.view_config. We can't reuse the
  // EntityDefinitionWire.definition_json shape (it carries entity_type,
  // fields, etc. — not `kanban`) without narrowing the type, so we
  // narrow here. When FR-ENT-10 lands an explicit `view_config` field
  // on the wire definition, this helper narrows to it directly.
  const parentShape = defJson as unknown as {
    view_config?: EntityViewConfigWire | null;
  };
  const v = parentShape?.view_config;
  if (!v) return null;
  return v;
}

/**
 * KanbanBoard — top-level Kanban renderer.
 *
 * The board owns the optimistic column state (a Map<recordId, value>)
 * and threads it through the card body. When a move fails, the board
 * removes the optimistic override so the card snaps back to its
 * server-supplied column. This implements AC-6 ("no silent column
 * jumps") without bypassing the server.
 */
export function KanbanBoard({
  entityType,
  definition,
  onConflict,
}: KanbanBoardProps): React.ReactElement {
  const { t } = useTranslation();

  // Resolve view_config. The default-view-type gate is in
  // EntityListPage; this component assumes it's already been gated.
  const viewConfig = resolveViewConfig(definition);
  const kanbanConfig: EntityKanbanConfigWire | null =
    viewConfig?.kanban ?? null;
  const groupByField = kanbanConfig?.group_by;
  // The definition version drives the cache key — a re-publication
  // invalidates the kanban window so a new column set re-renders.
  const viewConfigVersion = definition?.version ?? 0;
  const fields = definition?.definition_json?.fields;

  // Hook up drag-drop. The board owns the surface-level error
  // routing so the same three callbacks (onConflict / onValidation /
  // onFetchFailure) drive every drag-drop error path.
  const [validationRows, setValidationRows] = useState<
    | {
        row: { code?: string; field?: string; message: string };
      }[]
    | null
  >(null);

  const rollback = useCallback((recordId: string) => {
    setOptimisticColumn((prev) => {
      if (!prev.has(recordId)) return prev;
      const next = new Map(prev);
      next.delete(recordId);
      return next;
    });
  }, []);

  const onValidation = useCallback(
    (err: HttpValidationError, input: KanbanMoveInput) => {
      // Defensive — the rows can be 0..N. We render the FIRST row
      // as a one-element array so the ValidationError primitive
      // applies its standard chrome + per-row Card layout.
      const rows = Object.entries(err.errors).map(([fieldId, body]) => ({
        row: {
          // For non-field errors the key is "__non_field_error__";
          // we surface the synthetic field name so the audit trail
          // is preserved while the user-visible chrome remains
          // simple.
          field: fieldId === "__non_field_error__" ? undefined : fieldId,
          code: body.code,
          message:
            typeof body.message === "string" ? body.message : err.message,
        },
      }));
      setValidationRows(
        rows.length > 0
          ? rows
          : [
              {
                row: {
                  code: err.code ?? "validation_failed",
                  message: err.message,
                },
              },
            ],
      );
      rollback(input.recordId);
    },
    [rollback],
  );

  const onFetchFailure = useCallback(
    (_err: unknown, input: KanbanMoveInput) => {
      // Roll back the optimistic column placement on any non-typed
      // failure. The status-envelope surfacing of `lastError` will
      // happen at the next render (records-fetch path takes over).
      rollback(input.recordId);
    },
    [rollback],
  );

  const dragDrop = useKanbanDragDrop({
    entityType,
    viewConfigVersion,
    groupByField: groupByField ?? "",
    onConflict,
    onValidation,
    onFetchFailure,
  });

  // Records fetch — INV-1 / INV-2 enforced by the hook. The hook
  // returns only the server-stripped shape the card renders.
  const recordsQuery = useKanbanRecords({
    entityType,
    viewConfigVersion,
    enabled: Boolean(groupByField) && entityType.length > 0,
  });

  // Optimistic column placement. When a record is dropped onto a
  // column, the user sees the card in that column IMMEDIATELY;
  // the actual server PUT proceeds in the background. On a 4xx/5xx
  // the corresponding `rollback(recordId)` callback drops the entry
  // and the card snaps to the server's value on the next render.
  const [optimisticColumn, setOptimisticColumn] = useState<Map<string, string>>(
    new Map(),
  );

  // Patch the drop handler: on a successful fire, before
  // useKanbanDragDrop returns, we ALSO register the optimistic
  // override so the card flips to the destination column on the
  // next render. This is the "no silent column jumps" guarantee —
  // the rollback path is observable at the call site, the
  // optimistic update happens BEFORE the network round-trip.
  const wrappedOnColumnDrop = useCallback(
    (event: React.DragEvent<HTMLElement>, toValue: string) => {
      // Read the typed payload via the dataTransfer directly so we
      // can apply the optimistic state synchronously before the
      // mutation fires. The drop handler downstream also reads it
      // for the mutation input — both reads return the same data.
      const typed = event.dataTransfer?.getData("application/x-kanban-card");
      if (typed === "" || typed === undefined) {
        dragDrop.onColumnDrop(event, toValue);
        return;
      }
      const parts = typed.split("\u241F");
      const recordId = parts[0];
      const fromValue = parts[1];
      if (recordId && fromValue !== undefined && fromValue !== toValue) {
        setOptimisticColumn((prev) => {
          const next = new Map(prev);
          next.set(recordId, toValue);
          return next;
        });
      }
      // Clear any prior validation-error row surface — a new move
      // attempt resolves whatever was on screen for the prior fail.
      setValidationRows(null);
      dragDrop.onColumnDrop(event, toValue);
    },
    [dragDrop],
  );

  // Resolve the canvas columns from the enum definition.
  const columns = useMemo(
    () => resolveColumns(fields, groupByField ?? undefined),
    [fields, groupByField],
  );

  // Loading — the records query is in flight on the first render.
  if (
    recordsQuery.isLoading ||
    (recordsQuery.isFetching && !recordsQuery.data)
  ) {
    return (
      <SkeletonLayout
        data-testid="kanban-board--loading"
        aria-label={t("lists.title", "Records")}
      />
    );
  }

  // Error states — mirrors EntityListPage's ListErrorSurface. The
  // `validation-error` branch routes to the new primitive; the
  // remaining 5 branches reuse the existing fragments so the user's
  // chrome is identical across list view + kanban view.
  if (recordsQuery.error) {
    const status = classifyKanbanError(recordsQuery.error);
    if (status === "permission-denied") {
      return (
        <section
          data-testid="kanban-board--permission-denied"
          aria-label={t("lists.title", "Records")}
        >
          <PermissionDenied />
        </section>
      );
    }
    if (status === "stale-version") {
      return (
        <section
          data-testid="kanban-board--stale-version"
          aria-label={t("lists.title", "Records")}
        >
          <StaleVersionError />
        </section>
      );
    }
    if (status === "rate-limit") {
      return (
        <section
          data-testid="kanban-board--rate-limit"
          aria-label={t("lists.title", "Records")}
        >
          <RateLimitBackpressure
            retryAfterSeconds={recordsQuery.error.retryAfterSeconds}
          />
        </section>
      );
    }
    // fetch-failure (default)
    return (
      <section
        data-testid="kanban-board--fetch-failure"
        aria-label={t("lists.title", "Records")}
      >
        <FetchError
          message={t("lists.error.fetch_failure", "Could not load records.")}
          onRetry={() => {
            void recordsQuery.refetch();
          }}
        />
      </section>
    );
  }

  // Once `data` is present, surface-level validation rows that
  // arrived from the last drag-drop attempt take precedence over
  // the canvas (the user needs to dismiss the row before resuming
  // moves). This is a paired surface — the canvas remains visible
  // UNDER the rows, so the user can see the record set.
  if (validationRows !== null) {
    return (
      <section
        data-testid="kanban-board--validation-error"
        aria-label={t("lists.title", "Records")}
      >
        <ValidationError
          errors={validationRows.map((r) => r.row)}
          onRetry={() => setValidationRows(null)}
        />
      </section>
    );
  }

  // Ready — render the canvas.
  const records = (recordsQuery.data?.records ?? []) as KanbanRecord[];

  // Group the records by their effective column value. The
  // effective value is the optimistic override (when present) or
  // the server's current value at `record.fields[groupByField]`.
  const byColumn = useMemo(() => {
    const map = new Map<string, KanbanRecord[]>();
    for (const col of columns) {
      map.set(col.value, []);
    }
    for (const r of records) {
      const optimistic = optimisticColumn.get(r.id);
      const raw =
        optimistic !== undefined
          ? optimistic
          : (r.fields?.[groupByField ?? ""] as unknown);
      const key = typeof raw === "string" ? raw : "(unassigned)";
      // Records with an unknown / missing value land in a synthetic
      // "(unassigned)" bucket so the user sees them rather than
      // losing them — a record the server cannot place is still a
      // record the user may want to fix.
      const bucket = map.get(key) ?? map.get("(unassigned)") ?? [];
      bucket.push(r);
      map.set(key, bucket);
    }
    return map;
    // columns intentionally included so a definition refresh
    // re-keys the bucket map; optimisticColumn so a move re-keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, columns, optimisticColumn, groupByField]);

  const total = recordsQuery.data?.total ?? null;

  return (
    <section
      className="kanban-board"
      aria-label={t("lists.title", "Records")}
      data-testid="kanban-board"
    >
      <KanbanCountBanner
        shown={records.length}
        total={total}
        cap={KANBAN_RECORDS_CAP}
      />
      <div
        className="kanban-board-canvas"
        data-testid="kanban-board-canvas"
        style={{
          display: "flex",
          gap: "var(--space-4)",
          alignItems: "stretch",
          overflowX: "auto",
          padding: "var(--space-4)",
          background: "var(--surface-page)",
        }}
      >
        {columns.length === 0 ? (
          <p
            data-testid="kanban-board--no-columns"
            role="status"
            style={{
              padding: "var(--space-4)",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-sm)",
              color: "var(--text-tertiary)",
            }}
          >
            {t(
              "lists.kanban.no_columns",
              "Kanban is not configured for this entity type.",
            )}
          </p>
        ) : null}
        {columns.map((col) => {
          const colRecords = byColumn.get(col.value) ?? [];
          return (
            <KanbanColumn
              key={col.value}
              value={col.value}
              label={col.label}
              accentColor={col.accent}
              records={colRecords}
              fields={fields}
              groupByField={groupByField ?? ""}
              onCardDragStart={dragDrop.onCardDragStart}
              onDragOver={dragDrop.onColumnDragOver}
              onDrop={(event) => wrappedOnColumnDrop(event, col.value)}
              optimisticColumnValue={(id) => optimisticColumn.get(id)}
            />
          );
        })}
      </div>
    </section>
  );
}
